<!-- UnallocatedDutiesRow.vue -->
<template>
	<div class="scheduler-row unallocated-header-row border-bottom bg-grey-lighten-5">
		<v-snackbar v-model="snackbarState">
			{{ snackbarMsg }}

			<template #actions>
				<v-btn color="white" variant="text" icon="mdi-close" density="compact" @click="snackbarState = false" />
			</template>
		</v-snackbar>
		<div class="sidebar-column-sticky bg-grey-lighten-5 d-flex justify-space-between" :style="rowHeightStyle">
			<div class="sidebar-title d-flex justify-space-between align-center border-right pr-2 pl-3 h-100">
				<div class="text-body-2 font-weight-bold text-grey-darken-3">
					<span>{{ $t('unallocated_duties') }}</span>
					<span> ({{ totalCount }})</span>
				</div>
				<v-icon size="small" color="grey" class="pl-2" @click="onToggleExpand">mdi-chevron-down</v-icon>
			</div>
			<div v-if="props.isSidebarExpanded" class="sidebar-col-stats bg-grey-lighten-5 border-left" :style="dynamicStatsStyle"></div>

			<div class="resize-handle" :class="{ 'is-resizing': isResizing }" @pointerdown.prevent="onResizeStart" @dblclick.stop="customHeight = null" />
		</div>

		<div class="calendar-column-flow" :style="virtualPaddingStyle" @dblclick="handleDelegatedDblClick">
			<div
				v-for="day in virtualDays"
				:key="`unallocated-${day.isoDate}`"
				class="unallocated-day-cell unallocated-scroll border-right d-flex flex-column"
				:class="{ 'selected-day': day.isoDate === props.selectedDayIso }"
				:style="rowHeightStyle"
				:data-day-id="day.id"
				:data-iso-date="day.isoDate"
			>
				<template v-if="dutiesByDay[day.id]">
					<template v-for="duty in getDutiesList(dutiesByDay[day.id])" :key="duty.id">
						<div v-if="!duty.deleted" class="unallocated-duty-wrapper flex-shrink-0" :data-duty-id="duty.id">
							<div
								class="unallocated-duty-cell font-weight-bold"
								:class="{
									'preview-change': isDutyInPreview(day.id, duty),
									'preview-selected-change': isSelectedChangeLogDuty(day.id, duty),
									'preview-violation': isDutyInViolation(day.id, duty),
									'preview-selected-violation': isSelectedViolationDuty(day.id, duty),
								}"
								:style="getDutyCellStyle(duty)"
							>
								<DutyItem :duty="duty" :activeIndicators="getActiveIndicators" />
							</div>
						</div>
					</template>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import DutyItem from '../../Presentation/DutyItem.vue';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { usePlanningAssignDutyStore } from '@/rennovation/planning-view/store/usePlanningAssignDutyStore';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useDriverStatsStyles } from '@/rennovation/planning-view/composables/useDriverStatsStyles';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { usePreviewStore } from '@/rennovation/planning-view/store/usePreviewStore';
import { ref, shallowRef, computed, onMounted, onBeforeUnmount } from 'vue';
import type { CSSProperties } from 'vue';
import { useI18n } from 'vue-i18n';
import { VIOLATION_CATEGORIES, getDutyCellStyle, extractId } from './PlanningTableUtils';

const props = defineProps<{
	totalCount: number;
	isRowExpanded: boolean;
	isSidebarExpanded: boolean;
	virtualDays: any[];
	virtualPaddingStyle: any;
	dutiesByDay: Record<string, any[] | Record<string, any>>;
	selectedDayIso?: string;
}>();

const emit = defineEmits(['toggle-expand', 'select-duty']);

const { t } = useI18n();

const planningStore = usePlanningStore();
const assignDutyStore = usePlanningAssignDutyStore();
const dayStore = useDayStore();
const previewStore = usePreviewStore();

const snackbarState = ref(false);
const snackbarMsg = shallowRef('');

const getDutiesList = (dayDuties: any[] | Record<string, any>): any[] => {
	if (!dayDuties) {
		return [];
	}
	if (Array.isArray(dayDuties)) {
		return dayDuties;
	}
	return Object.values(dayDuties);
};

const getDutyKey = (duty) => duty?.original ?? duty?.id ?? duty?.uuid;

const buildUnallocatedDaySet = (dataMap: Record<string, any> | undefined): Set<string> => {
	const set = new Set<string>();
	if (!dataMap) {
		return set;
	}

	for (const [dayId, changeLogs] of Object.entries(dataMap)) {
		if (!changeLogs) {
			continue;
		}
		const arr = Array.isArray(changeLogs) ? changeLogs : [changeLogs];
		for (const changeLog of arr) {
			if (changeLog && !changeLog.user) {
				set.add(dayId);
			}
		}
	}
	return set;
};

const previewChangeSet = computed(() => {
	return buildUnallocatedDaySet(previewStore.changesMappedByDay);
});

const previewViolationSet = computed(() => {
	return buildUnallocatedDaySet(previewStore.previewViolationsMappedByDay);
});

const isDutyInPreview = (dayId: string, duty) => {
	const dutyKey = getDutyKey(duty);
	if (!dayId || dutyKey === undefined || previewStore.changesPanelActiveTab !== 'changes') {
		return false;
	}

	const logsForDay = previewStore.changesMappedByDay?.[dayId] ?? [];
	return logsForDay.some((log) => (log.affectedDuties ?? []).includes(dutyKey));
};

const isSelectedChangeLogDuty = (dayId: string, duty) => {
	const dutyKey = getDutyKey(duty);
	if (!dayId || dutyKey === undefined || previewStore.changesPanelActiveTab !== 'changes') {
		return false;
	}

	const changeLog = previewStore.selectedChangeLog as any;
	const logDayId = extractId(changeLog?.day ?? changeLog?.dayId);
	if (logDayId !== dayId) return false;

	return (changeLog?.affectedDuties ?? []).includes(dutyKey);
};

const isDutyInViolation = (dayId: string, duty) => {
	if (!dayId || previewStore.changesPanelActiveTab !== 'violations') {
		return false;
	}
	return previewViolationSet.value.has(dayId) && previewStore.changesMappedByDay[dayId].duty === duty.original;
};

const isSelectedViolationDuty = (dayId: string, duty) => {
	const changeLog = previewStore.selectedChangeLog as any;
	const logDayId = extractId(changeLog?.day ?? changeLog?.dayId);
	return (
		logDayId === dayId &&
		!changeLog?.user &&
		VIOLATION_CATEGORIES.includes(changeLog?.category) &&
		previewStore.changesPanelActiveTab === 'violations' &&
		duty?.original === changeLog.duty
	);
};

/* ----------------------------------------------------------------- */

const { dynamicStatsStyle } = useDriverStatsStyles(() => planningStore.filters.selectedStats, {
	columnWidth: 80,
	extraColumns: () => {
		return usePlanningBridgeStore().editor.superuser ? 1 : 0;
	},
});

const getActiveIndicators = computed(() => {
	const indicators = planningStore.filters.selectedIndicators;
	if (!Array.isArray(indicators)) {
		return [];
	}

	return indicators.filter((item: any) => {
		return item?.itemKey === 'exchangeDuty' && item?.groupKey === 'dutyFlags';
	});
});

const checkIfDayExists = (isoDate: string) => {
	const daysLookup = dayStore.days;
	const dayData = daysLookup[isoDate];
	if (dayData) {
		return true;
	}
	return false;
};

function handleDelegatedDblClick(event: MouseEvent) {
	const target = event.target as HTMLElement;

	const dutyCell = target.closest('[data-duty-id]') as HTMLElement | null;
	if (dutyCell) {
		const dutyId = dutyCell.dataset.dutyId;
		const dayCell = dutyCell.closest('.unallocated-day-cell') as HTMLElement | null;
		const dayId = dayCell?.dataset.dayId;

		if (dutyId && dayId && props.dutiesByDay[dayId]) {
			const list = getDutiesList(props.dutiesByDay[dayId]);
			const duty = list.find((d: any) => {
				return String(d.id) === String(dutyId);
			});
			if (duty) {
				emit('select-duty', duty);
			}
		}
		return;
	}

	const cellElement = target.closest('.unallocated-day-cell') as HTMLElement | null;
	if (!cellElement) {
		return;
	}

	if (target !== cellElement) {
		return;
	}

	const dayId = cellElement.dataset.dayId;
	const isoDate = cellElement.dataset.isoDate;
	if (!isoDate) {
		return;
	}

	if (!checkIfDayExists(isoDate)) {
		snackbarState.value = true;
		snackbarMsg.value = t('duty_create_error_without_day', { day: isoDate });
		return;
	}

	if (dayStore.getDayStatus(isoDate) === 'locked') {
		snackbarState.value = true;
		snackbarMsg.value = t('duty_create_error_locked_day');
		return;
	}

	if (!planningStore.canEditDutyForDate(isoDate)) {
		snackbarState.value = true;
		snackbarMsg.value = t('snackbar_info_text_locked_days');
		return;
	}

	assignDutyStore.openModal({
		date: isoDate,
		dayId: dayId,
		driverId: null,
		driverName: '',
		isLocked: false,
	});
}

const MIN_HEIGHT = 60;
const DEFAULT_HEIGHT = 90;
const EXPANDED_HEIGHT = 250;
const ABSOLUTE_MAX_HEIGHT = 400; // hard ceiling
const MAX_VIEWPORT_RATIO = 0.8;

const viewportHeight = ref(window.innerHeight);
const onWindowResize = () => {
	viewportHeight.value = window.innerHeight;
};

const maxHeight = computed(() => {
	return Math.max(MIN_HEIGHT, Math.min(ABSOLUTE_MAX_HEIGHT, Math.floor(viewportHeight.value * MAX_VIEWPORT_RATIO)));
});

const customHeight = ref<number | null>(null);
const isResizing = ref(false);

const rowHeight = computed(() => {
	const base = customHeight.value ?? (props.isRowExpanded ? EXPANDED_HEIGHT : DEFAULT_HEIGHT);
	return Math.min(maxHeight.value, Math.max(MIN_HEIGHT, base));
});
const rowHeightStyle = computed<CSSProperties>(() => {
	return { height: `${rowHeight.value}px` };
});

let startY = 0;
let startHeight = 0;

function onResizeStart(e: PointerEvent) {
	startY = e.clientY;
	startHeight = rowHeight.value;
	isResizing.value = true;

	(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
	window.addEventListener('pointermove', onResizeMove);
	window.addEventListener('pointerup', onResizeEnd);
	window.addEventListener('pointercancel', onResizeEnd);
	document.body.style.userSelect = 'none';
	document.body.style.cursor = 'row-resize';
}

function onResizeMove(e: PointerEvent) {
	const next = startHeight + (e.clientY - startY);
	customHeight.value = Math.min(maxHeight.value, Math.max(MIN_HEIGHT, next));
}

function onResizeEnd() {
	isResizing.value = false;
	window.removeEventListener('pointermove', onResizeMove);
	window.removeEventListener('pointerup', onResizeEnd);
	window.removeEventListener('pointercancel', onResizeEnd);
	document.body.style.userSelect = '';
	document.body.style.cursor = '';
}

function onToggleExpand() {
	customHeight.value = null;
	emit('toggle-expand');
}

onMounted(() => {
	window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
	onResizeEnd();
	window.removeEventListener('resize', onWindowResize);
});

onBeforeUnmount(onResizeEnd);
</script>

<style lang="scss" scoped>
.sidebar-title {
	width: var(--sidebar-width, 290px);
	min-width: var(--sidebar-width, 290px);
	max-width: var(--sidebar-width, 290px);
	flex: 0 0 var(--sidebar-width, 290px);
	box-sizing: border-box;
	font-weight: 700;
	font-size: 14px;
	color: #0f172a;
}
.calendar-body {
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
}

.scheduler-container {
	--row-height: 40px;
	--unallocated-height: 90px;
	--sidebar-width: 290px;
	--stats-width: 290px;
	--cell-width: 60px;
	--header-height: 35px;
	border: 1px solid #e2e8f0;
	height: calc(100vh - 150px);
	overflow: hidden;
	background-color: #ffffff;
	display: flex;
	flex-direction: column;
	position: relative;
}

.calendar-master-scroll-viewport {
	flex-grow: 1;
	overflow-x: auto;
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: #10565c #f1f1f1;
	position: relative;

	&::-webkit-scrollbar {
		width: 8px;
		height: 8px;
	}
	&::-webkit-scrollbar-track {
		background: #f1f1f1;
	}
	&::-webkit-scrollbar-thumb {
		background: #10565c;
		border-radius: 4px;
	}
}

.grid-render-overlay {
	position: fixed;
	top: calc(150px + 45px + 130px);
	left: var(--sidebar-width);
	right: 0;
	bottom: 0;
	background-color: rgba(255, 255, 255, 0.45);
	z-index: 99;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
}
.is-expanded .grid-render-overlay {
	left: calc(var(--sidebar-width) + var(--stats-width));
}

.scheduler-row {
	display: flex;
	width: max-content;
}

.calendar-header-sticky {
	position: sticky;
	top: 0;
	z-index: 20;
	background: white;
}

.sidebar-column-sticky {
	position: sticky;
	left: 0;
	z-index: 15;
	background-color: #ffffff;
	border-right: 1px solid #e2e8f0;
	display: flex;
	align-items: center;
	width: fit-content;
	flex-shrink: 0;
}
.is-expanded .sidebar-column-sticky {
	width: calc(var(--sidebar-width) + var(--stats-width));
}

.calendar-column-flow {
	display: flex;
	flex-direction: row;
	height: 100%;
	box-sizing: border-box;
}

.layout-month-header-row {
	height: 26px;
	background-color: #ffffff;

	.month-header-cell {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		padding-left: 12px;
		font-size: 11px;
		color: #475569;
		letter-spacing: 0.05em;
		border-right: 2px solid #cbd5e1;
		background-color: #f1f5f9;
		height: 100%;
	}
}

.layout-status-row {
	height: 16px;
	align-items: center;

	.sidebar-column-sticky {
		height: 100%;
	}
}

.layout-top-nav-row {
	height: 45px;
	background-color: #ffffff;
	display: flex;
	width: 100%;
}
.layout-date-row {
	height: 52px;
	background-color: #f8fafc;
}
.content-row {
	height: var(--row-height);
	contain: layout style;
	will-change: transform;
}

.sidebar-fixed-header {
	width: var(--sidebar-width) !important;
	flex-shrink: 0;
	border-right: 1px solid #e2e8f0;
	height: 100%;
}
.is-expanded .sidebar-fixed-header {
	width: calc(var(--sidebar-width) + var(--stats-width)) !important;
}

.navigation-track-fixed {
	flex-grow: 1;
	background-color: white;
	height: 100%;
	display: flex;
	align-items: center;
	border-bottom: 1px solid #e2e8f0;
}

.navigation-content {
	display: flex;
	align-items: center;
	padding-left: 16px;
	color: black;
	height: 100%;
	width: 100%;
}

.sidebar-col-stats {
	width: var(--stats-width);
	flex-shrink: 0;
	height: 100%;
	display: flex;
	font-size: 12px;
}

.empty-bar {
	width: var(--stats-width);
}
.planning-status-cell {
	width: var(--cell-width);
	height: 100%;
	flex-shrink: 0;
}

.day-cell {
	width: var(--cell-width);
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-right: 1px solid #f1f5f9;
	background: #f8fafc;
	flex-shrink: 0;

	.weekday-text {
		font-size: 9px;
		color: #94a3b8;
		font-weight: 600;
	}
	.day-number-text {
		font-size: 14px;
		font-weight: 700;
		color: #1e293b;
	}
}

.unallocated-day-cell {
	width: var(--cell-width);
	height: 100%;
	border-right: 1px solid #f1f5f9;
	display: flex;
	align-items: start;
	justify-items: center;
	flex-shrink: 0;
	contain: strict;
	padding: 2px 1px;
}

.unallocated-duty-wrapper {
	width: 100%;
	padding-bottom: 0px;
	box-sizing: border-box;
}

.unallocated-duty-cell {
	width: 100%;
	height: var(--row-height, 40px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	overflow: hidden;
	border: 1px solid #f1f5f9;
	border-radius: 0;
	background-color: transparent;
}

.unallocated-duty-inner {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.unallocated-duty-inner--gap {
	transform: scale(0.92);
}

.unallocated-scroll {
	overflow-y: auto;
	overflow-x: hidden;
	scrollbar-width: thin;
	scrollbar-color: #10565c #f1f1f1;

	&::-webkit-scrollbar {
		width: 4px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	&::-webkit-scrollbar-thumb {
		background: #cbd5e1;
		border-radius: 4px;
	}
	&::-webkit-scrollbar-thumb:hover {
		background: #94a3b8;
	}
}

.resize-handle {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 6px;
	cursor: row-resize;
	z-index: 16;
	touch-action: none;
	transition: background-color 0.15s;

	&:hover,
	&.is-resizing {
		background-color: #10565c;
		opacity: 0.5;
	}
}
.grid-cell {
	width: var(--cell-width);
	height: 100%;
	border-right: 1px solid #f1f5f9;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	contain: strict;
}

.saturday-cell {
	background-color: #f7f7f7;
}
.sunday-cell {
	background-color: #ebf9ff !important;
}
.highlighted-duty {
	background: #eeeeee;
}

.person-name {
	font-weight: 500;
	font-size: 13px;
	color: #1e293b;
}
.sidebar-title {
	font-weight: 700;
	font-size: 14px;
	color: #0f172a;
}
.border-bottom {
	border-bottom: 1px solid #e2e8f0;
}
.header-border-bottom {
	border-bottom: 1px solid #e2e8f0;
}

.status-past {
	background: #177a82;
}
.status-published {
	background: #4caf50;
}
.status-preview {
	background: #fdef96;
}
.status-locked {
	background: #b5b5b5;
}
.status-in-planning {
	background: #71dadf;
}
.status-open {
	background: #ebebeb;
}
.status-empty {
	background: transparent;
}
.border-right {
	border-right: 1px solid #eee;
}

.border-left {
	border-left: 1px solid #eee;
}

.chip-bar {
	width: 100%;
	max-width: var(--sidebar-width);
}
.selectable-row {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}
:deep .v-card-text {
	padding-right: 10px;
	padding-top: 6px;
	padding-left: 0;
}

:deep .v-checkbox {
	font-size: 14px !important;
}

:deep(.v-checkbox .v-selection-control) {
	width: 100%;
}

:deep(.v-checkbox .v-label) {
	width: 100%;
	opacity: 1;
}
.selected-day {
	background-color: #fff2e2 !important;
}

.selected-driver-row .grid-cell {
	background-color: #fff2e2 !important;
}

/* Preview highlighting */
.preview-change {
	outline: 3px solid #45a1f1ab;
	outline-offset: -2px;
	border-radius: 4px;
}

.preview-selected-change {
	outline: 3px solid #0960ed;
	outline-offset: -2px;
	border-radius: 4px;
}

.preview-violation {
	outline: 3px solid #e7000b66;
	outline-offset: -3px;
	border-radius: 4px;
}

.preview-selected-violation {
	outline: 3px solid #e7000b;
	outline-offset: -3px;
	border-radius: 4px;
}
</style>
